// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Sort from "@ces/XDL//Icons/Sort";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import { xBaseTheme } from "@ces/XDL/types";
import { color, getSystemColor } from "@ces/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import { DataTableCssOverrides } from "@insite/mobius/DataTable/DataTable";
import DataTablePresentationPropsDefault from "@insite/mobius/DataTable/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

const _borderStyle = `1px solid ${getSystemColor("common.border")}`;

export default {
    defaultProps: {
        ...DataTablePresentationPropsDefault,
        mergeCss: true,
        css: css`
            /* as border-collapse: collapse won't work with border-radius */
            border-collapse: separate;
            border-spacing: 0px;
            border: ${_borderStyle};
            /* to make sure we don't have any pixelation in the border-radius */
            overflow: hidden;
            border-radius: 2px;
            td[class^="DataTableCellBase"] {
                & > ${TypographyStyle}:empty:after {
                    content: "- -";
                }
            }
        `,
        cssOverrides: {
            cell: css`
                padding: 8px 12px;
                height: 38px;
                max-width: 242px;
                vertical-align: initial;
                /* reset border it will init by its own row */
                border-bottom: unset;
                &:not(:last-child) {
                    border-right: ${_borderStyle};
                }
                ${StyledButton}>${TypographyStyle} {
                    font-size: 14px;
                    line-height: 22px;
                }
            `,
            head: css`
                background: ${color("common.ui2")};
            `,
            header: css`
                background: ${color("common.ui11")};
                padding: 8px 12px;
                line-height: 14px;
                height: auto;
                vertical-align: baseline;
                border-bottom: ${_borderStyle};
                &:not(:last-child) {
                    border-right: ${_borderStyle};
                }
            `,
            row: css`
                font-size: 14px;
                line-height: 22px;
                /* per cell which not in last row, override the border-bottom */
                &:not(:last-child) > td {
                    border-bottom: ${_borderStyle};
                }
                :hover {
                    background: ${color("common.ui8", 0.2)};
                }
            `,
            evenRow: css`
                background: ${color("common.ui11")};
            `,
        } as DataTableCssOverrides,
        headerTypographyProps: {
            mergeCss: true,
            size: 12,
            weight: fontConfig.weight.bold,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: 14px;
            `,
        },
        cellTypographyProps: {
            mergeCss: true,
            size: 14,
            weight: fontConfig.weight.regular,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
        sortIconProps: {
            size: 11,
            color: getSystemColor("text.link"),
            css: css`
                &:hover {
                    color: ${color("common.ui8")};
                }
                margin-left: 16px;
            `,
        },
        sortIconSources: {
            ascending: Sort,
            descending: Sort,
            sortable: Sort,
        },
    },
} as xBaseTheme["dataTable"];
